import { LayoutDashboard, BookOpen, Layers, ClipboardList, Search, Heart, Settings, LogOut, Shield, Users, CreditCard, Palette, GraduationCap, FileText, Package } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAppSettings } from "@/hooks/useAppSettings";
import { Button } from "@/components/ui/button";

const studentItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Courses", url: "/courses", icon: BookOpen },
  { title: "Flashcards", url: "/decks", icon: Layers },
  { title: "Tests", url: "/tests", icon: ClipboardList },
  { title: "Dictionary", url: "/dictionary", icon: Search },
  { title: "Favorites", url: "/favorites", icon: Heart },
  { title: "Settings", url: "/settings", icon: Settings },
];

const adminItems = [
  { title: "Users", url: "/admin", icon: Users, end: true },
  { title: "Packages", url: "/admin/packages", icon: Package },
  { title: "Courses", url: "/admin/courses", icon: GraduationCap },
  { title: "Flashcard Decks", url: "/admin/decks", icon: Layers },
  { title: "Tests", url: "/admin/tests", icon: FileText },
  { title: "Payments", url: "/admin/payments", icon: CreditCard },
  { title: "Bank Settings", url: "/admin/bank-settings", icon: CreditCard },
  { title: "Brand", url: "/admin/brand", icon: Palette },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { brand } = useAppSettings();

  const items = isAdmin ? adminItems : studentItems;
  const groupLabel = isAdmin ? "Admin" : "Learn";
  const showLogo = !isAdmin && !!brand.logoUrl;
  const displayName = isAdmin ? "Admin Console" : brand.name || "Nihongo";
  const displayTagline = isAdmin ? "Platform management" : brand.tagline || "Master Japanese";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b">
        <NavLink to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-2 px-2 py-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow shrink-0 overflow-hidden">
            {showLogo ? (
              <img src={brand.logoUrl} alt={displayName} className="h-full w-full object-cover" />
            ) : isAdmin ? (
              <Shield className="h-5 w-5 text-primary-foreground" />
            ) : (
              <GraduationCap className="h-5 w-5 text-primary-foreground" />
            )}
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-sm">{displayName}</span>
              <span className="text-[10px] text-muted-foreground">{displayTagline}</span>
            </div>
          )}
        </NavLink>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item: any) => {
                const isActive = item.end
                  ? pathname === item.url
                  : pathname === item.url || pathname.startsWith(item.url + "/");
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <NavLink to={item.url} className="flex items-center gap-2">
                        <item.icon className="h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        {!collapsed && user && (
          <div className="px-2 py-2 text-xs">
            <div className="font-medium truncate">{user.email}</div>
            {isAdmin && <div className="text-[10px] text-primary mt-0.5">Administrator</div>}
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={signOut} className="justify-start gap-2">
          <LogOut className="h-4 w-4" />
          {!collapsed && "Sign out"}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
